import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, Container, Fade, IconButton, Modal } from '@material-ui/core'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import PartTable from './PartTable'
import {
  Apps,
  Close,
  FormatListBulleted,
  ImportExport,
  Search,
} from '@material-ui/icons'
import PartGrid from './PartGrid'
import { useInput } from '../../../hooks/useInput'
import SearchInput from '../../Form/SearchInput'
import { deletePart, getPartsByUserId } from '../../../actions/partActions'
import AddEditPart from '../AddEditPart'
import ToastMessage from '../../ToastMessage/ToastMessage'
import {
  PART_CREATE_RESET,
  PART_DELETE_RESET,
  PART_UPDATE_RESET,
} from '../../../constants/partConstants'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'

const PartLibrary = () => {
  const dispatch = useDispatch()

  const { bind: bindSearch, value: searchValue } = useInput('')
  const [viewType, setViewType] = useState('table')
  const [showAddEditPart, setShowAddEditPart] = useState(false)
  const [partToEdit, setPartToEdit] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [addPartToast, setAddPartToast] = useState(false)
  const [updatePartToast, setUpdatePartToast] = useState(false)
  const [updatedPartName, setUpdatedPartName] = useState('')
  const [createPartName, setCreatePartName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [partToDelete, setPartToDelete] = useState('')
  const [deletePartToast, setDeletePartToast] = useState(false)
  const [filteredParts, setFilteredParts] = useState([])
  const [sortValue, setSortValue] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [sorting, setSorting] = useState(false)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { loading, error, parts } = useSelector((state) => state.partList)
  const { createdPart, success: createPartSuccess } = useSelector(
    (state) => state.partCreate
  )
  const { success: partUpdateSuccess, part: updatedPart } = useSelector(
    (state) => state.partUpdate
  )
  const { success: successDelete } = useSelector((state) => state.partDelete)

  useEffect(() => {
    if (parts) {
      setFilteredParts(parts)
    }
  }, [dispatch, parts])

  useEffect(() => {
    if (searchValue !== '') {
      setFilteredParts(
        parts.filter((x) =>
          x.name.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    } else {
      setFilteredParts(parts)
    }
  }, [searchValue, setFilteredParts, parts])

  useEffect(() => {
    setSorting(true)

    if (sortValue === 'az') {
      setFilteredParts(
        filteredParts.sort((a, b) => a.name.localeCompare(b.name))
      )
    }

    if (sortValue === 'za') {
      setFilteredParts(
        filteredParts.sort((a, b) => b.name.localeCompare(a.name))
      )
    }

    if (sortValue === 'lastUpdated') {
      setFilteredParts(
        filteredParts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      )
    }

    if (sortValue === 'newest') {
      setFilteredParts(
        filteredParts.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      )
    }

    setTimeout(() => setSorting(false), 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilteredParts, sortValue, setSorting])

  useEffect(() => {
    dispatch(getPartsByUserId(userInfo._id))
    dispatch({ type: SIDE_NAV_CLOSE })

    if (createPartSuccess) {
      window.scrollTo(0, 0)
      setAddPartToast(true)
      setCreatePartName(createdPart.name)
      dispatch({ type: PART_CREATE_RESET })
    }

    if (partUpdateSuccess) {
      window.scrollTo(0, 0)
      setUpdatePartToast(true)
      setUpdatedPartName(updatedPart.name)
      setPartToEdit('')
      dispatch({ type: PART_UPDATE_RESET })
    }

    if (successDelete) {
      window.scrollTo(0, 0)
      dispatch(getPartsByUserId(userInfo._id))
      setDeletePartToast(true)
      dispatch({ type: PART_DELETE_RESET })
    }
  }, [
    updatedPart,
    createdPart,
    createPartSuccess,
    setUpdatedPartName,
    dispatch,
    partUpdateSuccess,
    successDelete,
    userInfo,
  ])

  const handleClose = () => {
    setPartToEdit('')
    setShowAddEditPart(false)
  }

  const confirmDeletePart = () => {
    dispatch(deletePart(partToDelete._id))
    setShowDeleteModal(false)
  }

  return (
    <div className="library gray-bg">
      <Container className="p-b-100">
        <h3 className="page-title accent-left">Part library</h3>

        <div className="flex flex-end m-b-16" style={{ height: 33 }}>
          {showSearch ? (
            <SearchInput bindValue={bindSearch} setShowSearch={setShowSearch} />
          ) : (
            <IconButton onClick={() => setShowSearch(true)}>
              <Search />
            </IconButton>
          )}
          <ActionsMenu
            icon={<ImportExport />}
            options={[
              { onClick: () => setSortValue('az'), label: 'A - Z' },
              { onClick: () => setSortValue('za'), label: 'Z - A' },
              { onClick: () => setSortValue('newest'), label: 'Newest' },
              {
                onClick: () => setSortValue('lastUpdated'),
                label: 'Last updated',
              },
            ]}
          ></ActionsMenu>
          {viewType === 'table' ? (
            <IconButton className="m-l-5" onClick={() => setViewType('grid')}>
              <Apps />
            </IconButton>
          ) : (
            <IconButton className="m-l-5" onClick={() => setViewType('table')}>
              <FormatListBulleted />
            </IconButton>
          )}
          <Button
            className="m-l-24"
            variant="contained"
            color="primary"
            onClick={() => setShowAddEditPart(true)}
          >
            Add part
          </Button>
        </div>

        {loading && <Loader />}

        {error && <Message variant="error">{error}</Message>}

        {!loading && !error && viewType === 'table' ? (
          <PartTable
            parts={filteredParts}
            setPartToEdit={setPartToEdit}
            setShowAddEditPart={setShowAddEditPart}
            setPartToDelete={setPartToDelete}
            setShowDeleteModal={setShowDeleteModal}
          />
        ) : (
          <PartGrid
            parts={filteredParts}
            setPartToEdit={setPartToEdit}
            setShowAddEditPart={setShowAddEditPart}
            setPartToDelete={setPartToDelete}
            setShowDeleteModal={setShowDeleteModal}
          />
        )}
      </Container>

      <Modal open={showAddEditPart} onClose={handleClose}>
        <Fade in={showAddEditPart}>
          <div className="modal-window full">
            <div className="modal-header">
              <div>{partToEdit === '' ? 'Add new' : 'Edit'} part</div>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <AddEditPart
                setShowAddPart={setShowAddEditPart}
                setPartToEdit={setPartToEdit}
                partToEdit={partToEdit}
              />
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Fade in={showDeleteModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Delete part?</div>
              <IconButton onClick={() => setShowDeleteModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Delete part?</h4>
              <p>
                Are you sure you want to delete {partToDelete.name}? This may
                impact any projects that are currently using this part.
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={confirmDeletePart}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Part added</p>
            <p className="fs14 m-b-0">
              You successfully created {createPartName}.
            </p>
          </>
        }
        open={addPartToast}
        setOpen={setAddPartToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Part updated</p>
            <p className="fs14 m-b-0">
              You successfully updated {updatedPartName}.
            </p>
          </>
        }
        open={updatePartToast}
        setOpen={setUpdatePartToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Part deleted</p>
            <p className="fs14 m-b-0">
              You successfully deleted {partToDelete.name}.
            </p>
          </>
        }
        open={deletePartToast}
        setOpen={setDeletePartToast}
      />
    </div>
  )
}

export default PartLibrary
